/**
 * 校驗登入後要跳轉的路徑
 * 只接受站內的絕對路徑，擋掉 //evil.com、https://evil.com 這類外站跳轉
 * @param {unknown} target 通常來自 route.query.redirect
 * @param {string} fallback 不合法時的預設去處
 */
export function safeRedirect(target, fallback = '/') {
  if (typeof target !== 'string' || !target) return fallback
  // 必須以單一 / 開頭：/xxx 可以，//evil.com、/\evil.com、http(s):// 都不行
  if (!/^\/(?![/\\])/.test(target)) return fallback
  return target
}

// 導向第三方前，把 state 與回程路徑暫存在 localStorage
// state 由後端簽發，回調時前後端各驗一次：後端驗簽名，前端比對是不是自己發起的那一次
// 不能用 sessionStorage：iOS 裝了 LINE 時會切去 App 自動登入，App 再用回調網址叫起
// Safari 開的是新分頁，sessionStorage 只活在原分頁，回來一律「登入驗證失敗」（踩過）
const PENDING_KEY = 'oauth_pending'
// 與後端 OAUTH_STATE_EXPIRES 對齊；localStorage 不會隨分頁關閉消失，得自己讓它過期
const PENDING_TTL_MS = 30 * 60 * 1000

function savePending(state, redirect) {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ state, redirect, at: Date.now() }))
  } catch {
    // 無痕模式等寫不進去的情況，回調時會落到「登入驗證失敗」，重新登入即可
  }
}

// 取出即刪：一個 state 只給一次回調用
function takePending() {
  try {
    const raw = localStorage.getItem(PENDING_KEY)
    localStorage.removeItem(PENDING_KEY)
    const pending = JSON.parse(raw || 'null')
    if (!pending || Date.now() - pending.at > PENDING_TTL_MS) return {}
    return pending
  } catch {
    return {}
  }
}

// 前台登入狀態：token 存 cookie（SSR 可讀），參與者資訊存 useState
// 目前僅支援 Google / LINE 第三方快速登入；永悅會員帳密登入之後再併進來
export function useAuth() {
  const api = useApi()
  // token 直接沿用 useApi 那一份，不要在這裡再 useCookie 一次：
  // 兩份 ref 會各自往同一個 cookie 寫，登入時誰最後寫進去是時序決定的（見 useApi.js）
  const { token, refreshToken, clearAuth } = api
  const user = useState('auth_user', () => null)

  const isLogged = computed(() => !!token.value)

  // 清掉這台瀏覽器上的登入狀態（換帳號登入與登出共用）
  function clearSession() {
    clearAuth()
    user.value = null
  }

  // 後端已完成設定的平台才會出現在清單裡，前台照著渲染按鈕即可
  function fetchProviders() {
    return api.get('/front/auth/providers')
  }

  /**
   * 發起第三方登入：向後端要授權網址，存下 state 後整頁跳轉
   * @param {'google'|'line'|'h2u'} platform
   * @param {string} redirect 登入完成後要回到的站內路徑
   */
  async function startSocialLogin(platform, redirect = '/member') {
    // 先把上一個帳號的憑證清乾淨：按下登入鈕就代表要換身分了，留著只會出事——
    // 舊的 access token 過期時會被 useApi 拿舊的 refresh token 換一張新的回來，
    // 跟這次登入拿到的那張互相打架（見 useApi.js 的 isAuthFlow）
    clearSession()
    // 帶上時戳讓每次都是一個新網址：這支每按一次就要拿到一個全新的 state，後端雖然
    // 已回 no-store，被設成忽略 Cache-Control 的 CDN 卻只認網址。快取一旦存住某一份，
    // 全站就共用同一個 state，它過期之後誰都登不進來，重按也是拿回同一個（踩過）
    const data = await api.get(`/front/auth/${platform}/url`, { _t: Date.now() })
    savePending(data.state, safeRedirect(redirect, '/member'))
    // 用 replace 讓使用者按上一頁時不會又被彈回授權頁
    window.location.replace(data.authUrl)
  }

  /**
   * 處理第三方導回：比對 state → 拿授權碼向後端換 token
   * @returns {Promise<string>} 登入完成後要去的站內路徑
   */
  async function finishSocialLogin(platform, { code, state }) {
    const { state: saved, redirect: savedRedirect } = takePending()
    const redirect = safeRedirect(savedRedirect, '/member')

    if (!code) throw new Error('登入資訊不完整，請重新登入')
    // 健行筆記的登入頁只認一個回程網址參數，沒有 state 這個欄位，我們也刻意不把它
    // 塞進回程網址（巢狀 query 常被這類登入頁截斷）。所以 h2u 回來時網址上沒有 state，
    // 退回自己存的那一份——它存在本站的 localStorage，別的網站寫不進來，
    // 仍證明得了這次登入是自己發起的
    const echo = state || saved
    // 本機完全沒有存 state 時放行，交給後端驗簽章（本站簽發、未過期、平台相符）：
    // LINE App 自動登入後一律叫起 Safari 的一般分頁，從私密瀏覽、iOS Chrome 或其他
    // App 內建瀏覽器發起的登入，回來時根本讀不到發起時存的那一份（踩過，私密瀏覽錄到影片）。
    // 有存卻對不上才擋——那才是「不是自己發起的這一次」
    if (!echo || (saved && saved !== echo)) throw new Error('登入驗證失敗，請重新登入')

    const data = await api.post(`/front/auth/${platform}/callback`, { code, state: echo })
    token.value = data.token
    refreshToken.value = data.refreshToken
    user.value = data.userInfo
    return redirect
  }

  // 後端 /front/auth/me 回傳 { userInfo }
  async function fetchUser() {
    if (!token.value) return null
    const data = await api.get('/front/auth/me')
    user.value = data.userInfo
    return user.value
  }

  /**
   * 更新個人資料（會員中心的個人資料頁）
   * 後端回的是整份最新資料，直接覆蓋，省得前台自己拼一份可能對不上的
   * @param {{nickname:string,gender?:string,birthday?:string,email?:string,region?:string}} payload
   */
  async function updateProfile(payload) {
    const data = await api.put('/front/auth/me', payload)
    user.value = data.userInfo
    return user.value
  }

  async function logout() {
    if (token.value) {
      // 後端撤銷 refreshToken；失敗不影響前端登出
      await api.post('/front/auth/logout').catch(() => {})
    }
    clearSession()
    // 登入入口是全站共用的登入彈窗，沒有獨立的登入頁，登出後回首頁
    await navigateTo('/')
  }

  return {
    token,
    user,
    isLogged,
    fetchProviders,
    startSocialLogin,
    finishSocialLogin,
    fetchUser,
    updateProfile,
    logout
  }
}
